import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, signal, WritableSignal, inject, viewChild, output, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Folder, SaveFolderInput } from '@core/types';
import { Store } from '@ngrx/store';
import { createFolderForSaving, deleteUserFolder, FoldersAPICall, FoldersAPILoadingState, getAllUserFolders, removeFolderForSaving, saveUserFolder, selectAllUserFolderAsTree, UserFoldersApiSignalService } from '@unified-ui/user-folders';
import { expandRecursive, flattenTreeNodes, getTopLevelParentNode } from '@unified-ui/utilities';
import { ConfirmationService, MenuItem, MenuItemCommandEvent, TreeDragDropService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { Tree, TreeNodeDropEvent } from 'primeng/tree';
import { map, tap, timer } from 'rxjs';
import { LoaderComponent } from '../loader/loader.component';

/**
 * Component for managing and displaying user folders inside save dialog.
 */
@Component({
    selector: 'shared-dialog-user-folders',
    standalone: true,
    imports: [AsyncPipe, FormsModule, SelectModule, Tree, LoaderComponent, TooltipModule, InputTextModule, ContextMenuModule, ButtonModule, ConfirmDialogModule],
    templateUrl: './dialog-user-folders.component.html',
    styleUrls: ['./dialog-user-folders.component.scss'],
    providers: [TreeDragDropService, ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogUserFoldersComponent {
    private _store = inject(Store);
    private _confirmationService = inject(ConfirmationService);
    private _loadingService = inject(UserFoldersApiSignalService);

    // Receives the report id to update folder selection
    public readonly reportOrScheduledId = input<string>('');
    // Emits the selected folder id to assign in the report saving
    public readonly selectedFolderId = output<string | null>();
    // Name input element reference
    protected readonly inlineNameInputRef = viewChild.required<ElementRef<HTMLInputElement>>('inlineNameInput');
    // Last context folder menu selected
    private _lastContextFolderMenu?: ContextMenu;
    // All user folders
    protected folders$ = this._store.select(selectAllUserFolderAsTree).pipe(
        // check expansions
        map(folderList => {
            if (this._keepExpansion) {
                folderList.map(node => {
                    return node.key === this._keepExpansion?.key ? this._expandNode(node) : node;
                });
            }
            return folderList;
        }),
        // Check in the current list if the report is assigned to one of the folders
        tap(folderList => {
            if (folderList?.length) {
                const assignedFolder = this._hasAssignedFolder(folderList);
                this.selectedFolderId.emit(assignedFolder?.key ?? null);
                if (assignedFolder) this.setSelectedFolder(assignedFolder, this._lastContextFolderMenu);
            }
        })
    );
    // Search in folders
    protected searchFolderValue = '';
    // Selected Folder
    protected selectedFolder?: TreeNode;
    // Editing Folder
    protected editingFolderNameId = signal('');
    // Name control during editing
    protected newFolderName = '';
    // loading control
    protected isLoading = computed(() => {
        return (
            this._loadingService.getLoadingState(FoldersAPICall.GET_ALL_USER_FOLDERS) === FoldersAPILoadingState.LOADING ||
            this._loadingService.getLoadingState(FoldersAPICall.SAVE_FOLDER) === FoldersAPILoadingState.LOADING ||
            this._loadingService.getLoadingState(FoldersAPICall.DELETE_FOLDER) === FoldersAPILoadingState.LOADING
        );
    });
    // flag to indicate if a new folder is being created to control ui input
    protected isFolderBeingCreated = false;
    // Node to keep expanded when updating the tree items
    private _keepExpansion?: TreeNode;

    // Context menu with folder options
    private _basicMenuItems: MenuItem[] = [
        {
            label: 'New Folder',
            command: () => {
                this.createNewFolder();
            }
        },
        {
            label: 'Edit',
            command: (event: MenuItemCommandEvent) => {
                event.originalEvent?.preventDefault();
                event.originalEvent?.stopPropagation();

                // check if there is a folder creation process to cancel
                if (this.isFolderBeingCreated) {
                    this.cancelFolderCreation();
                }
                this.newFolderName = this.selectedFolder?.label ?? '';
                this.editingFolderNameId.set(this.selectedFolder?.key ?? '');
            }
        }
    ];

    // Context menu with folder options
    protected completeMenuItems: WritableSignal<MenuItem[]> = signal([]);

    constructor() {
        this._store.dispatch(getAllUserFolders());
    }

    /**
     * Create a temporary folder to receive name editing before saving
     */
    protected createNewFolder() {
        // temporary folder
        const newSavingFolder: Folder = {
            id: 'saving_folder',
            name: '',
            parent: this.selectedFolder?.key ?? undefined
        };
        // set the parent expansion
        if (this.selectedFolder?.key) {
            this._keepExpansion = getTopLevelParentNode(this.selectedFolder);
        }
        // update component variables
        this.isFolderBeingCreated = true;
        this.newFolderName = '';
        this.editingFolderNameId.set('saving_folder');
        this._store.dispatch(createFolderForSaving({ folder: newSavingFolder }));
        this._scrollToInput();
    }

    /**
     * Save new folder
     */
    protected saveNewFolder(node: TreeNode) {
        // new folder input
        const newFolder: SaveFolderInput = {
            name: this.newFolderName,
            parentFolderId: node.parent?.key
        };
        // set the parent expansion
        if (node.parent?.key) {
            this._keepExpansion = getTopLevelParentNode(node);
        }
        this._clearComponentFolderVariables();

        // dispatch folder actions to save
        this._store.dispatch(saveUserFolder({ folder: newFolder }));
        this._store.dispatch(removeFolderForSaving());
    }

    /**
     * Cancel the saving of a new folder and remove the temporary folder in edit
     */
    protected cancelFolderCreation() {
        this._clearComponentFolderVariables();
        // dispatch to remove the temporary folder
        this._store.dispatch(removeFolderForSaving());
    }

    /**
     * Edit the folder name
     * @param newName new name
     */
    protected editFolderName(node?: TreeNode, newName?: string) {
        if (newName && node) {
            const editedFolder: SaveFolderInput = {
                id: node.key,
                name: newName
            };
            if (node.data.parent) {
                editedFolder.parentFolderId = node.data.parent;
            }
            this._keepExpansion = getTopLevelParentNode(node);
            this._store.dispatch(saveUserFolder({ folder: editedFolder }));
        }
        this._clearComponentFolderVariables();
    }

    /**
     *
     * Set the selected tree node
     */
    protected setSelectedFolder(node: TreeNode, contextMenu?: ContextMenu) {
        this.selectedFolder = node;
        this.hideLastContextFolderMenu();
        this._lastContextFolderMenu = contextMenu;
    }

    /**
     *
     * Hides the previous folder menu
     */
    protected hideLastContextFolderMenu() {
        if (this._lastContextFolderMenu) {
            this._lastContextFolderMenu.hide();
        }
    }

    /**
     * Reorder folder and change parent
     */
    protected editFolderParent(event: TreeNodeDropEvent) {
        this.selectedFolder = event.dragNode ?? undefined;

        const editedFolder: SaveFolderInput = {
            id: event.dragNode?.key,
            parentFolderId: event.dropNode?.key,
            name: this.selectedFolder?.data.name
        };
        this._keepExpansion = getTopLevelParentNode(event.dragNode!) ?? undefined;
        this._store.dispatch(saveUserFolder({ folder: editedFolder }));
    }

    /**
     * Tree tracking by element key
     */
    protected treeTrackBy(index: number, element: TreeNode): string | undefined {
        return element.key;
    }

    protected checkDeleting(node: TreeNode) {
        const hasReports = !!node.data?.reports?.length;
        const hasSchedules = !!node.data?.schedules?.length;
        const hasChildrenReports = !!node.children?.some(child => child.data?.reports?.length);
        const hasChildrenSchedules = !!node.children?.some(child => child.data?.schedules?.length);
        const isDeleteDisabled = hasReports || hasSchedules || hasChildrenReports || hasChildrenSchedules;

        this.completeMenuItems.set([
            ...this._basicMenuItems,
            {
                label: 'Delete',
                command: () => {
                    if (!isDeleteDisabled) {
                        this._deleteFolder();
                    }
                },
                tooltipOptions: {
                    tooltipLabel: isDeleteDisabled ? `This folder contains reports or schedules and can't be deleted` : undefined,
                    tooltipPosition: 'right'
                },
                styleClass: isDeleteDisabled ? 'context-menu-item-disabled' : undefined
            }
        ]);
    }

    /**
     * Confirm and call to delete a folder
     */
    private _deleteFolder() {
        // stores id because the selection on tree is lost when clicking in the confirm dialog
        const selectedId = this.selectedFolder?.key;
        this._confirmationService.confirm({
            key: 'folder-confirm-delete',
            header: 'Confirm Folder Deletion',
            message: `Delete Folder '${this.selectedFolder?.label}'?`,
            icon: 'pi pi-exclamation-circle',
            acceptButtonStyleClass: 'p-button-danger flex-row-reverse',
            acceptLabel: 'Delete',
            rejectButtonStyleClass: 'p-button-secondary p-button-text',
            rejectLabel: 'Cancel',
            accept: () => {
                if (selectedId) {
                    this._store.dispatch(deleteUserFolder({ id: selectedId }));
                }
            }
        });
        if (this.selectedFolder?.parent) {
            this._keepExpansion = getTopLevelParentNode(this.selectedFolder);
        }
    }

    // Node expansion
    private _expandNode(node: TreeNode): TreeNode {
        expandRecursive(node, true);
        return node;
    }

    /** Clear component variables of creating and editing */
    private _clearComponentFolderVariables() {
        this.isFolderBeingCreated = false;
        this.newFolderName = '';
        this.editingFolderNameId.set('');
        this.selectedFolder = undefined;
    }

    /**
     * Check if the current report or scheduled id is assigned to a folder
     * @param folderList
     * @returns Folder in tree | undefined
     */
    private _hasAssignedFolder(folderList: TreeNode<Folder>[]): TreeNode | undefined {
        const flatNodes = flattenTreeNodes(folderList);
        const hasReportFolder = flatNodes.find(node => node.data?.reports?.map((report: { id: string }) => report.id).includes(this.reportOrScheduledId()));
        const hasScheduleFolder = flatNodes.find(node => node.data?.schedules?.map((schedule: { id: string }) => schedule.id).includes(this.reportOrScheduledId()));
        // expand parent node
        if ((hasReportFolder && hasReportFolder.parent) || (hasScheduleFolder && hasScheduleFolder.parent)) {
            this._expandNode(getTopLevelParentNode(hasReportFolder ?? hasScheduleFolder!));
        }

        return hasReportFolder ?? hasScheduleFolder;
    }

    /**
     * Scrolls to the input element
     */
    private _scrollToInput(): void {
        // wait to the availability of the element in the DOM
        timer(200).subscribe(() => {
            const inlineNameInput = this.inlineNameInputRef()?.nativeElement;
            if (inlineNameInput) {
                inlineNameInput?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        });
    }
}
